import React, { useContext, useEffect, useMemo, useState } from "react";
import { Geometry } from "geojson";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditLegacyProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProviderLegacy";
import { MapContext } from "fond/map/MapProvider";
import { getCurrentProjectData } from "fond/project";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

import { DRAW_HINTS, getEditFeatures, makeEditDrawOptions, makeEditingLayers, makeEditToolbarConfig } from "../DataPrep/editConfig";

import { BASE_DATA_LAYER_IDS, BASE_DATA_LAYERS, BaseDataLayerKey } from "./layers";

export type BaseDataDrawParams = { layerKey: BaseDataLayerKey };

/** Return the geometry type of the layer being drawn, following the map's editing layer selection. */
const useEditingGeometryType = (initial: Geometry["type"]): Geometry["type"] => {
  const { map } = useContext(MapContext);
  const [geometryType, setGeometryType] = useState(initial);

  useEffect(() => {
    const handleLayerChange = (event: { geometryType?: Geometry["type"] }) => {
      if (event.geometryType) setGeometryType(event.geometryType);
    };
    map?.on("draw.layerchange", handleLayerChange);
    return () => {
      map?.off("draw.layerchange", handleLayerChange);
    };
  }, [map]);

  return geometryType;
};

/** Side panel screen for drawing and editing the features of one base data layer on the map. */
const BaseDataDraw: React.FC = () => {
  const { params } = useStackedNavigationContext();
  const { layerKey } = params["base-data-draw"] as BaseDataDrawParams;
  const { noun } = BASE_DATA_LAYERS[layerKey];
  const layerIds = BASE_DATA_LAYER_IDS[layerKey];
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));

  const layers = useMemo(() => makeEditingLayers(layerIds), [layerIds]);
  const options = useMemo(() => makeEditDrawOptions({ layers, editingLayerId: layerIds[0], editingLayerIds: layerIds }), [layers, layerIds]);
  const config = useMemo(() => makeEditToolbarConfig(layers.filter((layer) => !layer.Hide).map((layer) => layer.GeometryType)), [layers]);

  // Features from Redux (same source as MapContent) so IDs match what's on the map
  const initialFeatures = useMemo(
    () =>
      getEditFeatures(
        projectData?.layers,
        layers.map((layer) => layer.ID)
      ),
    [projectData, layers]
  );

  const geometryType = useEditingGeometryType(layers[0].GeometryType);

  return (
    <MultiLayerEditLegacyProvider versionId={versionId} options={options} config={config} initialFeatures={initialFeatures}>
      <MultiLayerEditPanelContent title={`Draw ${noun}`} alertTitle={DRAW_HINTS[geometryType]} />
    </MultiLayerEditLegacyProvider>
  );
};

export default BaseDataDraw;
